























































































import Vue from "vue";
import {
  EXERCISE_CONFIGS,
  VUEX_LEARNING_HISTORY_ACTION_NAMES,
  VUEX_LEARNING_HISTORY_MUTATION_NAMES,
  VUEX_LEARNING_HISTORY_NAMESPACE,
  VUEX_ROOT_MUTATION_NAMES
} from "@tt-app/constants";
import { DropdownOption, ExerciseType, Skill } from "@tt-app/models";

import { dateToYYYYMMDD, getSkillTitle } from "@tt-app/utils";

// @ts-ignore
import DatePicker from "v-calendar/lib/components/date-picker.umd";

import LayoutFullCard from "@tt-app/layouts/LayoutFullCard.vue";
import LearningHistoryExerciseLog from "@tt-app/components/learningHistory/LearningHistoryExerciseLog.vue";
import { LearningHistoryExerciseLog as LearningHistoryExerciseLogRecord } from "@tt-app/models";

import iconBook from "@joyz/app-core/assets/icons/default/Book.svg";
import iconCalendar from "@joyz/app-core/assets/icons/default/Calendar.svg";
import iconChevronDown from "@joyz/app-core/assets/icons/transparent/ChevronDown.svg";

const { EXERCISE_LOGS_REQUEST } = VUEX_LEARNING_HISTORY_ACTION_NAMES;

interface Icons {
  [key: string]: string | Icons;
}

interface Data {
  endDate: Date;
  icons: Icons;
  selectedExerciseLogTypeAndSkill: { exerciseType: number; exerciseSkill: number };
  selectedCompoundExerciseType: string;
  startDate: Date;
  storedDatesProcessed: boolean;
}

interface Methods {
  ensureEndDateValidity: () => void;
  ensureStartDateValidity: () => void;
  fetchExerciseLogs: () => void;
  processDates: () => void;
  weeksAgo: (weeksAgo: number) => Date;
}

interface Computed {
  exerciseLogs: any[];
  compoundExerciseTypes: DropdownOption[] | undefined;
  storedEndDate: Date;
  storedStartDate: Date;
}

export default Vue.extend<Data, Methods, Computed>({
  name: "LearningHistoryExerciseLogs",
  components: {
    DatePicker,
    LayoutFullCard,
    LearningHistoryExerciseLog
  },
  filters: {
    displayDate(date: Date) {
      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      const day = date.getDate();

      return `${year}/${month}/${day}`;
    }
  },
  data() {
    return {
      icons: {
        book: iconBook,
        calendar: iconCalendar,
        chevronDown: iconChevronDown
      },
      endDate: new Date(),
      selectedExerciseLogTypeAndSkill: { exerciseType: 0, exerciseSkill: 0 },
      selectedCompoundExerciseType: `{0-0}`,
      startDate: new Date(new Date().valueOf() - 60 * 60 * 24 * 14 * 1000),
      storedDatesProcessed: false
    };
  },
  computed: {
    exerciseLogs() {
      return this.$store.getters["learningHistory/exerciseLogsByDate"];
    },
    compoundExerciseTypes() {
      const allExerciseType = {
        label: `${this.$__("exercise.type.0")}`,
        value: "0-0"
      };
      const allLogs = this.$store.state.learningHistory
        .exerciseLogs as LearningHistoryExerciseLogRecord[];
      if (!allLogs || allLogs.length == 0) {
        return [allExerciseType, { label: "", value: this.selectedCompoundExerciseType }];
      }
      const inUseCompoundExerciseTypes = new Set(
        allLogs.map(exerciseLog => {
          return `${exerciseLog.exercise.type}-${exerciseLog.exercise.skill}`;
        })
      );

      const individualCompoundExerciseTypes = Object.entries(EXERCISE_CONFIGS)
        .flatMap(outerConfig => {
          const exerciseType = (outerConfig[0] as unknown) as ExerciseType;
          const config = outerConfig[1];
          return Object.entries(config).map(innerConfig => {
            const skill = (innerConfig[0] as unknown) as Skill;
            const functionString = this.$__(getSkillTitle(exerciseType, skill));
            const typeString = this.$__(`exercise.type.${exerciseType}`);
            return {
              label: `${functionString} ${typeString}`,
              value: `${exerciseType}-${skill}`
            };
          });
        })
        .filter(compoundKey => {
          return inUseCompoundExerciseTypes.has(compoundKey.value);
        });
      return [allExerciseType, ...individualCompoundExerciseTypes];
    },
    storedEndDate() {
      return this.$store.getters["learning_history_end_date"] as Date;
    },
    storedStartDate() {
      return this.$store.getters["learning_history_start_date"] as Date;
    }
  },
  watch: {
    endDate: {
      handler(endDate) {
        if (!endDate) {
          return;
        }

        this.$store.commit(`${VUEX_ROOT_MUTATION_NAMES.LEARNING_HISTORY_END_DATE}`, endDate);
        this.fetchExerciseLogs();
      },
      immediate: false
    },
    selectedCompoundExerciseType: {
      handler(compoundExerciseType) {
        const [exerciseType, exerciseSkill] = String(compoundExerciseType).split("-");
        this.selectedExerciseLogTypeAndSkill = {
          exerciseType: parseInt(exerciseType),
          exerciseSkill: parseInt(exerciseSkill)
        };
      }
    },
    selectedExerciseLogTypeAndSkill: {
      handler(exerciseTypeAndSkill) {
        this.$store.commit(
          `${VUEX_LEARNING_HISTORY_NAMESPACE}/${VUEX_LEARNING_HISTORY_MUTATION_NAMES.SELECTED_EXERCISE_LOG_TYPE_AND_SKILL}`,
          exerciseTypeAndSkill
        );
      }
    },
    startDate: {
      handler(startDate) {
        if (!startDate) {
          return;
        }

        this.$store.commit(`${VUEX_ROOT_MUTATION_NAMES.LEARNING_HISTORY_START_DATE}`, startDate);
        this.fetchExerciseLogs();
      },
      immediate: false
    }
  },
  created() {
    this.selectedExerciseLogTypeAndSkill = this.$store.state.learningHistory.selectedExerciseLogTypeAndSkill;
    this.selectedCompoundExerciseType = `${this.selectedExerciseLogTypeAndSkill.exerciseType}-${this.selectedExerciseLogTypeAndSkill.exerciseSkill}`;
    this.processDates();
  },
  methods: {
    fetchExerciseLogs() {
      if (!this.storedDatesProcessed || !this.startDate || !this.endDate) {
        return;
      }

      this.$store.dispatch(`${VUEX_LEARNING_HISTORY_NAMESPACE}/${EXERCISE_LOGS_REQUEST}`, {
        startDate: dateToYYYYMMDD(this.startDate),
        endDate: dateToYYYYMMDD(this.endDate),
        lang: "ja"
      });
    },
    async ensureEndDateValidity() {
      if (this.storedEndDate) {
        const minimumEndDate = this.startDate;
        const maximumEndDate = new Date();

        let endDate = undefined;
        if (this.storedEndDate <= this.startDate) {
          endDate = minimumEndDate;
        } else if (this.storedEndDate > maximumEndDate) {
          endDate = maximumEndDate;
        } else {
          endDate = this.storedEndDate;
        }

        this.endDate = endDate;
      }
    },
    async ensureStartDateValidity() {
      if (this.storedStartDate) {
        const maximumStartDate = new Date();

        let startDate = undefined;
        if (this.storedStartDate > maximumStartDate) {
          startDate = maximumStartDate;
        } else {
          startDate = this.storedStartDate;
        }

        this.startDate = startDate;
      }
    },
    async processDates() {
      await Promise.all([this.ensureStartDateValidity(), this.ensureEndDateValidity()]);

      this.storedDatesProcessed = true;

      this.fetchExerciseLogs();
    },
    weeksAgo(weeksAgo: number) {
      const today = new Date();
      const oneWeek = 60 * 60 * 24 * 7 * 1000;
      const timeFrame = oneWeek * weeksAgo;
      return new Date(today.valueOf() - timeFrame);
    }
  }
});
